// Le chronomètre du terrain (D-098, maquettes 02 et 03), repris de Playbook BCL : plein écran sombre, lisible
// de loin. Le score de 2 à 10 équipes, chacune son nom et sa couleur, comme Playbook (+1, +2, +3, −1, remise à 0 ;
// rien n'est enregistré), jusqu'à 4 chronos (le chrono actif en grand, les autres en petit), les préréglages de
// l'équipe qui arment ET lancent le chrono actif. Fin d'un décompte : le son choisi sur ce téléphone, la vibration,
// l'écran rouge. Ajout d'Arceau : le chronomètre qui monte, et les chasubles des groupes de l'exercice qui deviennent
// les équipes du score. En paysage : le score à gauche, les chronos à droite.
// L'état (chronos, score) reste le temps de la séance, sur ce téléphone : refermer puis rouvrir le retrouve.
import './drills.css';
import type { Team } from '../lib/api';
import { setTeamChrono, teamChrono } from '../lib/drills-api';
import {
  CHRONO_MAX, PRESETS_MAX, TEAMS_MAX, TEAMS_MIN, addPoints, addScoreTeam, arm, chipMain, fmtTimer, isRunning, newTimer, pause, readTeamChrono,
  removeScoreTeam, reset, scoreTeams, stateLabel, storedChrono, teamName, tensionFreq, tick, toggle, tone, valueMs, type Preset, type ScoreTeam,
  type TeamChrono, type Timer,
} from '../domain/chrono';
import { END_SOUNDS, alarm, flashPref, holdScreen, playEndSound, setFlashPref, setSoundPref, setTensionPref, soundPref, tensionPref, tick as tickSound, unlockAudio } from './drill-engine';
import { $, $$, html, mount, openSheet, toast, type Html } from './dom';

export interface ChronoOpts {
  team: Team | null;
  /** Les chasubles des groupes de l'exercice en cours (séance en direct, ou celle de l'heure). */
  bibs?: ScoreTeam[];
}

// L'état du terrain : il survit à la fermeture de l'écran (le temps de la séance).
let timers: Timer[] = [];
let activeId = '';
/** Le score de chaque équipe, à sa place (la 1re, la 2e…). */
let scores: number[] = [];
let seq = 0;
let host: HTMLElement | null = null;
const uid = () => `t${++seq}`;

export async function openChrono(opts: ChronoOpts): Promise<void> {
  unlockAudio();
  if (host) return;
  host = document.createElement('div');
  host.className = 'ch-full';
  host.setAttribute('role', 'dialog');
  host.setAttribute('aria-label', 'Chronomètre');
  document.body.append(host);
  const el = host;
  let setting: TeamChrono = readTeamChrono({});
  const release = holdScreen();
  let ticker = 0;
  const lastSec = new Map<string, number>();
  const now = () => Date.now();
  const active = () => timers.find(t => t.id === activeId) ?? timers[0]!;
  const ensure = () => {
    if (!timers.length) timers = [newTimer(uid(), 'down', setting.presets[0])];
    if (!timers.some(t => t.id === activeId)) activeId = timers[0]!.id;
  };
  const teams = () => scoreTeams(setting, opts.bibs);

  const close = () => {
    // Refermé : les chronos en cours s'arrêtent là où ils en sont (comme Playbook).
    timers = timers.map(t => pause(t, now()));
    clearInterval(ticker);
    document.removeEventListener('keydown', onKey);
    release();
    el.remove();
    host = null;
  };
  const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !document.querySelector('.dr-over')) close(); };
  document.addEventListener('keydown', onKey);

  const timeCls = (t: Timer) => `t-${tone(t, now())}`;
  const ctrl = (t: Timer): Html => isRunning(t)
    ? html`<button type="button" class="ch-pause" data-toggle="${t.id}">❚❚ Pause</button>`
    : html`<button type="button" class="ch-go" data-toggle="${t.id}">▶ ${t.done ? 'Relancer' : 'Start'}</button>`;
  const sub = (t: Timer) => `${t.kind === 'up' ? 'le temps monte' : 'compte à rebours'} · ${stateLabel(t).replace('● ', '')}`;

  const draw = () => {
    ensure();
    const a = active(), others = timers.filter(t => t !== a), tm = teams();
    // 3 équipes et plus : cartes en deux colonnes (la dernière, seule, sur toute la largeur) ; 5 et plus, cartes basses.
    const many = tm.length >= 5 ? ' is-many' : tm.length >= 3 ? ' is-more' : '';
    mount(el, html`<div class="ch-in">
      <div class="ch-top"><span class="k"><span class="ti ti-chrono" aria-hidden="true"></span>Chronomètre${opts.team ? ` · ${opts.team.name}` : ''}</span>
        <span class="ch-ibs"><button type="button" class="ch-ib" data-set aria-label="Réglages du chrono" data-guide="ch-settings">⚙</button><button type="button" class="ch-ib" data-close aria-label="Fermer le chronomètre">×</button></span></div>
      <section class="ch-l">
      <div class="ch-sh"><span>Score</span><button type="button" data-zero>↺ Tout à 0</button></div>
      <div class="ch-score${many}" data-guide="ch-score">${tm.map((t, i) => html`
        <div class="ch-team" style="--c:${t.color}"><span class="nm"><i></i><b>${t.name}</b></span><span class="sc" data-sc="${i}">${scores[i] ?? 0}</span>
          <div class="ch-pts">${[1, 2, 3].map(n => html`<button type="button" data-pt="${i}:${n}" aria-label="${t.name} +${n}">+${n}</button>`)}</div>
          <div class="ch-corr"><button type="button" data-pt="${i}:-1" aria-label="${t.name} moins 1">−1</button><button type="button" data-zero1="${i}" aria-label="${t.name} à zéro">↺ 0</button></div></div>`)}</div>
      </section>
      <section class="ch-r">
      <div class="ch-sh"><span>Chronos</span>${timers.length < CHRONO_MAX ? html`<button type="button" data-add>+ Ajouter</button>` : ''}</div>
      <div class="ch-card" data-guide="ch-active">
        <div class="hd"><span>${a.label}<span class="act">actif</span>${timers.length > 1 ? html`<button type="button" class="rm" data-rm="${a.id}" aria-label="Retirer ce chrono">✕</button>` : ''}</span><small data-ts="${a.id}">${stateLabel(a)}</small></div>
        <div class="ch-time ${timeCls(a)}" data-tv="${a.id}" aria-live="off">${fmtTimer(a, now())}</div>
        <div class="ch-ctrl">${ctrl(a)}<button type="button" class="ch-reset" data-reset="${a.id}">↺ ${a.kind === 'down' ? chipMain(Math.round(a.totalMs / 1000)) : '0'}</button></div>
      </div>
      ${others.map(t => html`<div class="ch-mini" data-pick="${t.id}" role="button" tabindex="0" aria-label="Rendre actif : ${t.label}">
        <span class="l"><strong>${t.label}</strong><small data-ts2="${t.id}">${sub(t)}</small></span>
        <span class="v ${timeCls(t)}" data-tv="${t.id}">${fmtTimer(t, now())}</span>
        <button type="button" class="b ${isRunning(t) ? 'pz' : ''}" data-toggle="${t.id}" aria-label="${isRunning(t) ? 'Pause' : 'Départ'}">${isRunning(t) ? '❚❚' : '▶'}</button></div>`)}
      <div class="ch-sh"><span>Préréglages → chrono actif</span><button type="button" data-set>⚙ Gérer</button></div>
      <div class="ch-presets" data-guide="ch-presets">${setting.presets.map((p, i) => html`<button type="button" data-preset="${i}"><b>${chipMain(p.seconds)}</b><span>${p.label || chipMain(p.seconds)}</span></button>`)}</div>
      </section>
    </div>`);
    wire();
    run();
  };
  const paintValues = () => {
    for (const t of timers) {
      const v = el.querySelector<HTMLElement>(`[data-tv="${t.id}"]`);
      if (!v) continue;
      v.textContent = fmtTimer(t, now());
      const big = v.classList.contains('ch-time');
      v.className = `${big ? 'ch-time' : 'v'} ${timeCls(t)}`;
    }
  };
  const run = () => {
    clearInterval(ticker);
    if (!timers.some(isRunning)) return;
    ticker = window.setInterval(() => {
      let ended = false;
      timers = timers.map(t => {
        const r = tick(t, now());
        if (r.fired) { ended = true; alarm(); }
        if (isRunning(r.timer) && r.timer.kind === 'down' && tensionPref()) {
          const sec = Math.ceil(valueMs(r.timer, now()) / 1000), f = tensionFreq(sec);
          if (f && lastSec.get(t.id) !== sec) { lastSec.set(t.id, sec); tickSound(f); }
          if (sec > 3) lastSec.delete(t.id);
        }
        return r.timer;
      });
      if (ended) draw(); else paintValues();
    }, 100);
  };
  const wire = () => {
    $(el, '[data-close]')!.addEventListener('click', close);
    for (const b of $$(el, '[data-set]')) b.addEventListener('click', () => openSettings(opts, setting, s => { setting = s; draw(); }));
    $(el, '[data-zero]')!.addEventListener('click', () => { scores = []; draw(); });
    for (const b of $$<HTMLButtonElement>(el, '[data-pt]')) b.addEventListener('click', () => {
      const [i, d] = b.dataset.pt!.split(':').map(Number) as [number, number];
      scores[i] = addPoints(scores[i] ?? 0, d);
      el.querySelector(`[data-sc="${i}"]`)!.textContent = String(scores[i]);
    });
    for (const b of $$<HTMLButtonElement>(el, '[data-zero1]')) b.addEventListener('click', () => {
      const i = Number(b.dataset.zero1); scores[i] = 0; el.querySelector(`[data-sc="${i}"]`)!.textContent = '0';
    });
    for (const b of $$<HTMLButtonElement>(el, '[data-toggle]')) b.addEventListener('click', e => {
      e.stopPropagation(); unlockAudio();
      activeId = b.dataset.toggle!;   // toucher un chrono le rend actif
      timers = timers.map(t => (t.id === activeId ? toggle(t, now()) : t));
      draw();
    });
    for (const b of $$<HTMLButtonElement>(el, '[data-reset]')) b.addEventListener('click', () => { timers = timers.map(t => (t.id === b.dataset.reset ? reset(t) : t)); draw(); });
    for (const b of $$<HTMLButtonElement>(el, '[data-rm]')) b.addEventListener('click', () => { timers = timers.filter(t => t.id !== b.dataset.rm); draw(); });
    for (const m of $$<HTMLElement>(el, '[data-pick]')) {
      const pick = () => { activeId = m.dataset.pick!; draw(); };
      m.addEventListener('click', pick);
      m.addEventListener('keydown', e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); pick(); } });
    }
    for (const b of $$<HTMLButtonElement>(el, '[data-preset]')) b.addEventListener('click', () => {
      unlockAudio();
      const p = setting.presets[Number(b.dataset.preset)];
      if (!p) return;
      timers = timers.map(t => (t.id === activeId ? arm(t, p, now()) : t));
      lastSec.delete(activeId);
      try { navigator.vibrate?.(20); } catch { /* pas de vibreur */ }
      draw();
    });
    $(el, '[data-add]')?.addEventListener('click', () => {
      openSheet('Ajouter un chrono', html`<div class="dr-kinds">
        <button type="button" class="dr-kind" data-k="down"><span aria-hidden="true">⏳</span><span><strong>Compte à rebours</strong><small>${setting.presets[0] ? `${setting.presets[0].label} · ${chipMain(setting.presets[0].seconds)}, puis un préréglage` : 'Puis un préréglage'}</small></span></button>
        <button type="button" class="dr-kind" data-k="up"><span aria-hidden="true">⏱</span><span><strong>Chronomètre</strong><small>Le temps monte : un exercice, un défi</small></span></button></div>`,
      (sheet, closeSheet) => {
        sheet.parentElement!.classList.add('dr-over');
        for (const b of $$<HTMLButtonElement>(sheet, '[data-k]')) b.addEventListener('click', () => {
          const t = newTimer(uid(), b.dataset.k === 'up' ? 'up' : 'down', b.dataset.k === 'up' ? undefined : setting.presets[0]);
          timers = [...timers, t].slice(0, CHRONO_MAX);
          activeId = t.id;
          closeSheet(); draw();
        });
      });
    });
  };

  draw();
  if (opts.team) {
    setting = readTeamChrono(await teamChrono(opts.team.id).catch(() => ({})));
    // Les préréglages de l'équipe remplacent ceux par défaut tant que rien n'a été lancé.
    if (timers.length === 1 && !isRunning(timers[0]!) && timers[0]!.baseMs === timers[0]!.totalMs && timers[0]!.kind === 'down' && setting.presets[0]) {
      timers = [newTimer(timers[0]!.id, 'down', setting.presets[0])];
    }
    if (host === el) draw();
  }
}

/** Réglages du chrono (maquette 03) : préréglages et équipes du score pour toute l'équipe ; sons sur ce téléphone.
 *  Les équipes nommées, comme Playbook : de 2 à 10, chacune son nom et sa couleur (ajouter, renommer, colorer, retirer). */
function openSettings(opts: ChronoOpts, setting: TeamChrono, onChange: (s: TeamChrono) => void): void {
  let presets: Preset[] = setting.presets.map(p => ({ ...p }));
  let score: TeamChrono['score'] = { mode: setting.score.mode, teams: setting.score.teams.map(t => ({ ...t })) };
  let saveT = 0;
  const bibs = (opts.bibs?.length ?? 0) >= TEAMS_MIN ? opts.bibs!.slice(0, TEAMS_MAX).map(b => b.name).join(' · ') : null;
  const namesSub = () => `${score.teams.map(t => t.name).join(' · ')} : de ${TEAMS_MIN} à ${TEAMS_MAX} équipes`;
  const persist = () => {
    const next: TeamChrono = { presets: presets.filter(p => p.seconds >= 1), score };
    onChange({ ...next, presets: next.presets.length ? next.presets : setting.presets });
    if (!opts.team) return;
    clearTimeout(saveT);
    saveT = window.setTimeout(() => {
      void setTeamChrono(opts.team!.id, storedChrono(next)).catch(() => toast('Enregistrement impossible : réservé au staff de l’équipe.', 'ko'));
    }, 600);
  };
  openSheet('Réglages du chrono', html`<div id="ch-set"></div>`, (sheet) => {
    sheet.parentElement!.classList.add('dr-over');
    const box = $(sheet, '#ch-set')!;
    const draw = () => {
      mount(box, html`
        <div class="sheet-sec" style="border-top:0;padding-top:0"><h4 class="dr-sec-h">Préréglages <small>${opts.team ? `pour toute l’équipe ${opts.team.name}` : 'sur ce téléphone'}</small></h4>
          ${presets.map((p, i) => html`<div class="ch-prow">
            <button type="button" class="h" data-up="${i}" aria-label="Monter ${p.label}" ${i === 0 ? 'disabled' : ''}>⋮⋮</button>
            <input data-l="${i}" value="${p.label}" maxlength="24" aria-label="Nom du préréglage">
            <span class="sec"><input data-s="${i}" type="number" inputmode="numeric" min="1" max="5400" value="${p.seconds}" aria-label="Secondes"><small>s</small></span>
            <button type="button" class="rm" data-rm="${i}" aria-label="Retirer ${p.label}">×</button></div>`)}
          ${presets.length < PRESETS_MAX ? html`<button type="button" class="btn btn-ghost btn-sm" data-addp style="justify-self:start">+ Préréglage</button>` : ''}</div>
        <div class="sheet-sec"><h4>Les équipes du score</h4>
          <div class="ch-radio">
            <label class="${score.mode === 'bibs' ? 'on' : ''}"><input type="radio" name="ch-mode" value="bibs" ${score.mode === 'bibs' ? 'checked' : ''}><span class="o"></span>
              <span><strong>Les chasubles de l’exercice</strong><small>${bibs ? `${bibs}, repris des groupes de la séance` : 'Une équipe par chasuble des groupes de la séance (sinon tes noms)'}</small></span></label>
            <label class="${score.mode === 'names' ? 'on' : ''}"><input type="radio" name="ch-mode" value="names" ${score.mode === 'names' ? 'checked' : ''}><span class="o"></span>
              <span><strong>Des noms fixes</strong><small data-tnames>${namesSub()}</small></span></label>
          </div>
          ${score.mode === 'names' ? html`<div class="ch-trows">${score.teams.map((t, i) => html`<div class="ch-trow">
              <label class="sw" style="--c:${t.color}"><input type="color" data-tc="${i}" value="${t.color}" aria-label="Couleur de ${t.name}"></label>
              <input data-n="${i}" maxlength="20" value="${t.name}" placeholder="${teamName(i)}" aria-label="Nom de l’équipe ${i + 1}">
              ${score.teams.length > TEAMS_MIN ? html`<button type="button" class="rm" data-trm="${i}" aria-label="Retirer ${t.name}">×</button>` : html`<span></span>`}</div>`)}
            ${score.teams.length < TEAMS_MAX ? html`<button type="button" class="btn btn-ghost btn-sm" data-addt>+ Ajouter une équipe</button>` : ''}</div>` : ''}</div>
        <div class="sheet-sec"><h4 class="dr-sec-h">Le son de fin <small>sur ce téléphone</small></h4>
          <div class="ch-sounds">${END_SOUNDS.map(s => html`<button type="button" class="ch-sound" data-snd="${s.id}" aria-pressed="${String(soundPref() === s.id)}">
            <span><strong>${s.name}</strong><small>${s.sub}</small></span><span class="p" data-play="${s.id}" aria-label="Écouter ${s.name}">▶</span></button>`)}</div>
          <div class="dr-swrow"><span><strong>Tic-tac 3, 2, 1</strong><small>sur les trois dernières secondes</small></span><button type="button" class="iv-switch" role="switch" data-sw="tension" aria-checked="${String(tensionPref())}" aria-label="Tic-tac 3, 2, 1"></button></div>
          <div class="dr-swrow"><span><strong>Écran rouge à zéro</strong><small>se voit de loin, même sans le son</small></span><button type="button" class="iv-switch" role="switch" data-sw="flash" aria-checked="${String(flashPref())}" aria-label="Écran rouge à zéro"></button></div>
        </div>`);
      for (const i of $$<HTMLInputElement>(box, '[data-l]')) i.addEventListener('change', () => { presets[Number(i.dataset.l)]!.label = i.value.trim().slice(0, 24); persist(); });
      for (const i of $$<HTMLInputElement>(box, '[data-s]')) i.addEventListener('change', () => {
        const v = Math.round(Number(i.value));
        if (!(v >= 1 && v <= 5400)) { toast('Entre 1 et 5 400 secondes.', 'ko'); i.value = String(presets[Number(i.dataset.s)]!.seconds); return; }
        presets[Number(i.dataset.s)]!.seconds = v; persist();
      });
      for (const b of $$<HTMLButtonElement>(box, '[data-rm]')) b.addEventListener('click', () => { presets.splice(Number(b.dataset.rm), 1); persist(); draw(); });
      for (const b of $$<HTMLButtonElement>(box, '[data-up]')) b.addEventListener('click', () => {
        const i = Number(b.dataset.up); if (i < 1) return;
        [presets[i - 1], presets[i]] = [presets[i]!, presets[i - 1]!]; persist(); draw();
      });
      $(box, '[data-addp]')?.addEventListener('click', () => { presets = [...presets, { label: '', seconds: 30 }]; draw(); box.querySelector<HTMLInputElement>(`[data-l="${presets.length - 1}"]`)?.focus(); });
      for (const r of $$<HTMLInputElement>(box, 'input[name="ch-mode"]')) r.addEventListener('change', () => { score = { ...score, mode: r.value === 'names' ? 'names' : 'bibs' }; persist(); draw(); });
      for (const i of $$<HTMLInputElement>(box, '[data-n]')) i.addEventListener('change', () => {
        const k = Number(i.dataset.n);
        score.teams[k]!.name = i.value.trim().slice(0, 20) || teamName(k);
        i.value = score.teams[k]!.name; persist();
        $(box, '[data-tnames]')!.textContent = namesSub();
      });
      for (const i of $$<HTMLInputElement>(box, '[data-tc]')) {
        // La pastille suit le doigt ; l'enregistrement part au choix final.
        i.addEventListener('input', () => { i.parentElement!.style.setProperty('--c', i.value); });
        i.addEventListener('change', () => { score.teams[Number(i.dataset.tc)]!.color = i.value.toLowerCase(); persist(); });
      }
      for (const b of $$<HTMLButtonElement>(box, '[data-trm]')) b.addEventListener('click', () => {
        const k = Number(b.dataset.trm);
        score = { ...score, teams: removeScoreTeam(score.teams, k) };
        scores.splice(k, 1);   // les scores des suivantes restent à leur équipe
        persist(); draw();
      });
      $(box, '[data-addt]')?.addEventListener('click', () => {
        score = { ...score, teams: addScoreTeam(score.teams) };
        persist(); draw();
        const inp = box.querySelector<HTMLInputElement>(`[data-n="${score.teams.length - 1}"]`);
        inp?.focus(); inp?.select();
      });
      for (const b of $$<HTMLButtonElement>(box, '[data-snd]')) b.addEventListener('click', () => {
        unlockAudio(); const id = b.dataset.snd as (typeof END_SOUNDS)[number]['id'];
        setSoundPref(id); playEndSound(id);
        $$(box, '[data-snd]').forEach(x => x.setAttribute('aria-pressed', String(x === b)));
      });
      for (const b of $$<HTMLButtonElement>(box, '[data-sw]')) b.addEventListener('click', () => {
        const on = b.getAttribute('aria-checked') !== 'true';
        b.setAttribute('aria-checked', String(on));
        if (b.dataset.sw === 'tension') setTensionPref(on); else setFlashPref(on);
      });
    };
    draw();
  });
}
